<template>
  <div class="ei-level-employees-chart">
    <div class="ei-level-employees-chart__header">
      <h3 class="ei-level-employees-chart__title">
        {{ title }}
      </h3>

      <p
          v-if="description"
          class="ei-level-employees-chart__description"
      >
        {{ description }}
      </p>
    </div>

    <div ref="chartWrapper" class="ei-level-employees-chart__body">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script>
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale,
  Tooltip,
} from 'chart.js'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip
)

const DEFAULT_COLORS = ['#F55565', '#FF9F40', '#98D970', '#17B20A']

const valueLabelPlugin = {
  id: 'valueLabelPlugin',

  afterDatasetsDraw(chart) {
    const { ctx } = chart
    const meta = chart.getDatasetMeta(0)

    ctx.save()
    ctx.font = '600 11px sans-serif'
    ctx.fillStyle = '#ffffff'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    meta.data.forEach((bar, index) => {
      const value = chart.data.datasets[0].data[index]

      if (value === null || value === undefined) {
        return
      }

      const isZero = Number(value) === 0

      ctx.fillStyle = isZero ? 'rgba(0, 0, 0, 0.45)' : '#ffffff'
      ctx.fillText(String(value), bar.x, isZero ? bar.base - 8 : bar.y + ((bar.base - bar.y) / 2))
    })

    ctx.restore()
  },
}

export default {
  name: 'EiLevelEmployeesChart',

  props: {
    title: {
      type: String,
      default: 'Количество сотрудников на каждом уровне Общего ЭИ',
    },

    description: {
      type: String,
      default: '',
    },

    items: {
      type: Array,
      default: () => [],
    },

    maxValue: {
      type: Number,
      default: null,
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    const chartWrapper = ref(null)
    const chartInstance = ref(null)
    const resizeObserver = ref(null)

    const destroyChart = () => {
      if (chartInstance.value) {
        chartInstance.value.destroy()
        chartInstance.value = null
      }
    }

    const resizeChart = () => {
      if (!chartInstance.value) {
        return
      }

      chartInstance.value.resize()
    }

    const renderChart = () => {
      destroyChart()

      if (!chartCanvas.value) {
        return
      }

      const labels = props.items.map((item) => item.label)
      const values = props.items.map((item) => Number(item.value) || 0)
      const colors = props.items.map((item, index) => item.color || DEFAULT_COLORS[index] || '#D5DBE2')
      const maxFromValues = values.length ? Math.max(...values) : 0
      const suggestedMax = props.maxValue || Math.ceil((maxFromValues + 8) / 5) * 5

      chartInstance.value = new Chart(chartCanvas.value, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: colors,
              borderRadius: 4,
              borderSkipped: false,
              minBarLength: 2,
              barPercentage: 0.94,
              categoryPercentage: 0.94,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          layout: {
            padding: {
              top: 4,
              right: 0,
              bottom: 0,
              left: 0,
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            datalabels: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context) => `${context.label}: ${context.parsed.y}`,
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.06)',
                drawBorder: false,
              },
              ticks: {
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
              },
              border: {
                display: false,
              },
            },
            y: {
              beginAtZero: true,
              suggestedMax,
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.06)',
                drawBorder: false,
              },
              ticks: {
                precision: 0,
                stepSize: 5,
                color: 'rgba(0, 0, 0, 0.55)',
                font: {
                  size: 12,
                },
              },
              border: {
                display: false,
              },
            },
          },
        },
        plugins: [valueLabelPlugin],
      })
    }

    onMounted(() => {
      nextTick(() => {
        renderChart()

        if (chartWrapper.value && typeof ResizeObserver !== 'undefined') {
          resizeObserver.value = new ResizeObserver(() => {
            resizeChart()
          })

          resizeObserver.value.observe(chartWrapper.value)
        }
      })
    })

    onBeforeUnmount(() => {
      if (resizeObserver.value) {
        resizeObserver.value.disconnect()
        resizeObserver.value = null
      }

      destroyChart()
    })

    watch(
        () => [
          props.items,
          props.maxValue,
        ],
        () => {
          nextTick(renderChart)
        },
        {
          deep: true,
        }
    )

    return {
      chartCanvas,
      chartWrapper,
    }
  },
}
</script>

<style lang="scss" scoped>
.ei-level-employees-chart {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.ei-level-employees-chart__header {
  max-width: 500px;
  margin: 0 0 20px;

  @media (max-width: $tablet-breakpoint) {
    max-width: unset;
  }
}

.ei-level-employees-chart__title {
  margin: 0 0 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-level-employees-chart__description {
  margin: 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.ei-level-employees-chart__body {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 245px;
  overflow: hidden;
}

.ei-level-employees-chart__body canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
  max-width: 100%;
}

@media (max-width: $mobile-breakpoint) {
  .ei-level-employees-chart__body {
    height: 240px;
  }
}
</style>
